/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFlags } from 'sarsaparilla';
import { addressKey, canonicalizeAddress } from '../utils/address-utils';
import {
    cancelPassReview,
    connectExistingPass,
    deleteMigratedPass,
    getPass,
    getPassByReservationNumber,
    getPassDetails,
    getPassesByIds,
    getUserPassIdList,
    getUserPasses,
    submitPassReview,
    verifyAddress,
    type SubmitPassReviewPayload,
    type ConnectPassParams,
    type ConnectPassProxyFor,
} from './interagency-pass-api';

export const USER_PASSES_KEY = 'user-passes';
export const USER_PASS_ID_LIST_KEY = 'user-pass-id-list';
export const USER_PASSES_PAGE_KEY = 'user-passes-page';
export const CONNECT_PASS_KEY = 'connect-pass';
export const VERIFY_ADDRESS_KEY = 'verify-address';
export const PASS_DETAILS_KEY = 'pass-details';
export const PASS_RECORD_KEY = 'pass-record';
export const RESERVATION_LOOKUP_KEY = 'reservation-lookup';
export const DELETE_MIGRATED_PASS_KEY = 'delete-migrated-pass';
export const SUBMIT_PASS_REVIEW_KEY = 'submit-pass-review';
export const CANCEL_PASS_REVIEW_KEY = 'cancel-pass-review';

export const ATB_PASSES_PAGE_SIZE = 10;

/**
 * React Query hook to fetch user's interagency passes
 *
 * Returns all passes for the current user. Filtering can be done on the frontend
 * by status or other criteria for better performance and user experience.
 *
 * @param enabled - Whether the query should be enabled/run
 * @returns Query result with user's interagency passes
 */
export function useUserPassesQuery(
    enabled: boolean,
    params?: { is_migrated_pass?: boolean }
) {
    return useQuery({
        queryKey: [USER_PASSES_KEY, params],
        queryFn: async ({ signal }) => getUserPasses(params, signal),
        staleTime: 3000,
        refetchOnWindowFocus: true,
        retry: false,
        enabled,
    });
}

/**
 * React Query hook to fetch the list of pass IDs for the current user.
 * Used for paginated display — gives us the total count and IDs without fetching full pass data.
 */
export function useUserPassIdListQuery(
    status?: 'active' | 'expired',
    enabled: boolean = true
) {
    return useQuery({
        queryKey: [USER_PASS_ID_LIST_KEY, status],
        queryFn: async ({ signal }) => getUserPassIdList(status, signal),
        staleTime: 3000,
        refetchOnWindowFocus: true,
        retry: false,
        enabled,
    });
}

/**
 * React Query hook to fetch a page of passes by their IDs.
 * Only fetches the passes needed for the current page.
 */
export function usePassesPageQuery(ids: string[], enabled: boolean) {
    return useQuery({
        queryKey: [USER_PASSES_PAGE_KEY, ids],
        queryFn: async ({ signal }) => getPassesByIds(ids, signal),
        staleTime: 3000,
        refetchOnWindowFocus: true,
        retry: false,
        enabled: enabled && ids.length > 0,
    });
}

/**
 * React Query hook to look up a pass by reservation number
 *
 * Only runs when a non-empty reservation number is provided and enabled is true.
 *
 * @param reservationNumber - The reservation number to search for
 * @param enabled - Whether the query should be enabled/run
 * @returns Query result with the matching pass
 */
export function useReservationLookupQuery(reservationNumber: string, enabled: boolean) {
    return useQuery({
        queryKey: [RESERVATION_LOOKUP_KEY, reservationNumber],
        queryFn: async ({ signal }) =>
            getPassByReservationNumber(reservationNumber, signal),
        enabled: enabled && reservationNumber.length > 0,
        retry: false,
        refetchOnWindowFocus: false,
    });
}

// ----------------------------------------
// Address Verification Query (PII-safe hashed key)
// ----------------------------------------
type VerifyParams = {
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip_code: string;
};

// canonicalizeAddress and addressKey are provided by utils/address-utils

export function useVerifyAddressQuery(input: VerifyParams | null, enabled: boolean) {
    const key = (() => {
        if (!input) return [VERIFY_ADDRESS_KEY, 'disabled'] as const;
        return addressKey(canonicalizeAddress(input));
    })();

    const isRunnable = Boolean(
        enabled && input && input.address1 && input.city && input.state && input.zip_code
    );

    return useQuery({
        queryKey: key,
        queryFn: ({ signal }) => verifyAddress(input as VerifyParams, signal),
        enabled: isRunnable,
        staleTime: Infinity,
        gcTime: Infinity,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });
}

/**
 * React Query hook to fetch pass details for all available pass types
 *
 * Returns all pass details for display in the FindYourPass component.
 * This data is relatively static and doesn't change frequently.
 *
 * Filters pass types based on the `residentPassTypeEnabled` feature flag:
 * - Flag ON (true): Filters OUT 'annual' pass type (shows resident/nonresident)
 * - Flag OFF (false): Filters OUT 'resident' and 'nonresident' pass types (shows annual)
 *
 * @returns Query result with filtered pass details for available pass types
 */
export function usePassDetailsQuery() {
    const flags = useFlags();
    const { releaseDigitalIapFeatures } = flags;

    // LD is ready when flags have been loaded (non-empty object)
    // Using flag count is more reliable than useLDClient() due to React context timing
    const isLDReady = Object.keys(flags).length > 0;

    const residentPassTypeEnabled = isLDReady
        ? (releaseDigitalIapFeatures?.residentPassTypeEnabled ?? false)
        : false;

    const query = useQuery({
        queryKey: [PASS_DETAILS_KEY, { residentPassTypeEnabled }],
        queryFn: getPassDetails,
        enabled: isLDReady, // Don't run until flags are loaded
        // staleTime: 0, // Defaults to 0
        // gcTime: 0, // Defaults to 5 minutes
        retry: 2,
        refetchOnWindowFocus: false,
        select: (data) => {
            if (residentPassTypeEnabled) {
                // Flag ON: filter OUT 'annual'
                return data.filter((pass) => pass.type !== 'annual');
            }
            // Flag OFF: filter OUT 'resident' and 'nonresident'
            return data.filter(
                (pass) => pass.type !== 'resident' && pass.type !== 'nonresident'
            );
        },
    });

    // Override isLoading to include waiting for LD to be ready
    // This ensures consuming components show loading state while waiting for flags
    return {
        ...query,
        isLoading: !isLDReady || query.isLoading,
    };
}

/**
 * React Query hook to fetch pass by ID
 *
 * @param id - The pass ID to fetch
 * @param enabled - Whether the query should be enabled/run
 * @param staleTime - How long the data is considered fresh (default: 3000ms)
 * @returns Query result with pass data
 */
export function usePassQuery(id: string, enabled: boolean, staleTime: number = 3000) {
    return useQuery({
        queryKey: [PASS_RECORD_KEY, id],
        queryFn: () => getPass(id),
        staleTime,
        refetchOnWindowFocus: true,
        retry: false,
        enabled,
    });
}

/** Mutation payload for connecting an existing physical pass. */
export type ConnectPassMutationParams = ConnectPassParams;

// Re-export for convenience
export type { ConnectPassProxyFor };

/**
 * React Query mutation hook to connect an existing physical pass to user's account
 *
 * Provides optimistic updates and automatic refetching of user passes on success.
 * Use this hook to connect physical passes that users already own to their digital account.
 *
 * @returns Mutation object with mutate function and status
 */
export function useConnectPassMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [CONNECT_PASS_KEY],
        mutationFn: connectExistingPass,
        onSuccess: () => {
            // Refetch user passes to get the newly connected pass
            queryClient.invalidateQueries({ queryKey: [USER_PASSES_KEY] });
            queryClient.invalidateQueries({ queryKey: [USER_PASS_ID_LIST_KEY] });
            queryClient.invalidateQueries({ queryKey: [USER_PASSES_PAGE_KEY] });
        },
        retry: false,
    });
}

/**
 * React Query mutation hook to delete a migrated pass by ID
 *
 * Calls the delete migrated pass API and invalidates the user passes query on success.
 *
 * @returns Mutation object with mutate function and status
 */
export function useDeleteMigratedPassMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [DELETE_MIGRATED_PASS_KEY],
        mutationFn: (passId: string) => deleteMigratedPass(passId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [USER_PASSES_KEY] });
            queryClient.invalidateQueries({ queryKey: [USER_PASS_ID_LIST_KEY] });
            queryClient.invalidateQueries({ queryKey: [USER_PASSES_PAGE_KEY] });
        },
        retry: false,
    });
}

/**
 * Parameters for the submit pass review mutation
 */
interface SubmitPassReviewParams {
    passId: string;
    payload: SubmitPassReviewPayload;
}

/**
 * Parameters for the cancel pass review mutation
 */
type CancelPassReviewParams = string;

/**
 * React Query mutation hook to submit a pass review for approval
 *
 * Called after documents have been uploaded for an ACTION-REQUIRED pass.
 * Invalidates both the user passes list and the specific pass record on success.
 *
 * @returns Mutation object with mutate function and status
 */
export function useSubmitPassReviewMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [SUBMIT_PASS_REVIEW_KEY],
        mutationFn: ({ passId, payload }: SubmitPassReviewParams) =>
            submitPassReview(passId, payload),
        onSuccess: (_data, { passId }) => {
            // Invalidate both the user passes list and the specific pass record
            queryClient.invalidateQueries({ queryKey: [USER_PASSES_KEY] });
            queryClient.invalidateQueries({ queryKey: [USER_PASS_ID_LIST_KEY] });
            queryClient.invalidateQueries({ queryKey: [USER_PASSES_PAGE_KEY] });
            queryClient.invalidateQueries({ queryKey: [PASS_RECORD_KEY, passId] });
        },
        retry: false,
    });
}

/**
 * React Query mutation hook to cancel a pass review
 *
 * Called after a user clicks on the "Cancel Application button"
 * Invalidates both the user passes list and the specific pass record on success.
 *
 * @returns Mutation object with mutate function and status
 */
export function useCancelPassReviewMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [CANCEL_PASS_REVIEW_KEY],
        mutationFn: (passId: CancelPassReviewParams) => cancelPassReview(passId),
        onSuccess: (_data, passId) => {
            // Invalidate both the user passes list and the specific pass record
            queryClient.invalidateQueries({ queryKey: [USER_PASSES_KEY] });
            queryClient.invalidateQueries({ queryKey: [USER_PASS_ID_LIST_KEY] });
            queryClient.invalidateQueries({ queryKey: [USER_PASSES_PAGE_KEY] });
            queryClient.invalidateQueries({ queryKey: [PASS_RECORD_KEY, passId] });
        },
        retry: false,
    });
}
